import { useEffect, useRef } from "react";
import { z } from "zod";

/* Notificações em tempo real da fila interna, por Server-Sent Events (`GET /api/tickets/eventos`).

   O `EventSource` reconecta sozinho quando a conexão cai, e a API manda `retry: 5000`. Esta camada
   só faz duas coisas: validar o que chega (um quadro fora do contrato é descartado, não quebra a
   tela) e entregar à tela um aviso em português. */

export const CAMINHO_DA_FILA_AO_VIVO = "/api/tickets/eventos";

export const TIPOS_DE_EVENTO_DA_FILA = [
  "chamado_novo",
  "resposta_do_cliente",
  "chamado_reaberto",
  "chamado_distribuido",
] as const;
export type TipoDeEventoDaFila = (typeof TIPOS_DE_EVENTO_DA_FILA)[number];

const esquemaDoEvento = z.object({
  ticket_id: z.string().uuid(),
  numero: z.number().int(),
  assunto: z.string(),
  ocorrido_em: z.string(),
});
export type EventoDaFila = z.infer<typeof esquemaDoEvento> & { tipo: TipoDeEventoDaFila };

const FRASES: Readonly<Record<TipoDeEventoDaFila, string>> = {
  chamado_novo: "Novo chamado",
  resposta_do_cliente: "O cliente respondeu no chamado",
  chamado_reaberto: "Chamado reaberto",
  chamado_distribuido: "Chamado distribuído para você ou para a equipe",
};

/** A frase do aviso: "Novo chamado #12 — Erro ao emitir pedido". */
export function fraseDoEvento(evento: EventoDaFila): string {
  return `${FRASES[evento.tipo]} #${evento.numero} — ${evento.assunto}`;
}

/** Lê um quadro SSE. Devolve `null` para o que não é do contrato. */
export function lerEvento(tipo: string, dados: string): EventoDaFila | null {
  if (!(TIPOS_DE_EVENTO_DA_FILA as readonly string[]).includes(tipo)) return null;
  let bruto: unknown;
  try {
    bruto = JSON.parse(dados);
  } catch {
    return null;
  }
  const analise = esquemaDoEvento.safeParse(bruto);
  return analise.success ? { ...analise.data, tipo: tipo as TipoDeEventoDaFila } : null;
}

/** A chave que identifica um evento: a mesma mensagem reentregue na reconexão tem a mesma chave. */
export function chaveDoEvento(evento: EventoDaFila): string {
  return `${evento.tipo}|${evento.ticket_id}|${evento.ocorrido_em}`;
}

/**
 * Liga a fila ao vivo enquanto `ativo`. `aoEvento` fica num ref: trocar a função não derruba a
 * conexão. `aoConexao` diz se o fluxo está de pé (abriu) ou caiu (reconectando). Sem `EventSource`
 * no ambiente (navegador antigo, teste) não faz nada.
 *
 * Na reconexão o navegador devolve o `Last-Event-ID` e a API reentrega o que passou na janela —
 * por isso quem consome descarta por `chaveDoEvento` o que já viu.
 */
export function useFilaAoVivo(
  ativo: boolean,
  aoEvento: (evento: EventoDaFila) => void,
  aoConexao?: (conectado: boolean) => void,
): void {
  const ultimo = useRef(aoEvento);
  const ultimaConexao = useRef(aoConexao);
  useEffect(() => {
    ultimo.current = aoEvento;
    ultimaConexao.current = aoConexao;
  });

  useEffect(() => {
    if (!ativo || typeof EventSource === "undefined") return;
    const fonte = new EventSource(CAMINHO_DA_FILA_AO_VIVO, { withCredentials: true });
    fonte.onopen = () => ultimaConexao.current?.(true);
    fonte.onerror = () => ultimaConexao.current?.(false);
    const ouvintes = TIPOS_DE_EVENTO_DA_FILA.map((tipo) => {
      const ouvinte = (mensagem: Event) => {
        const evento = lerEvento(tipo, (mensagem as MessageEvent<string>).data);
        if (evento) ultimo.current(evento);
      };
      fonte.addEventListener(tipo, ouvinte);
      return [tipo, ouvinte] as const;
    });
    return () => {
      for (const [tipo, ouvinte] of ouvintes) fonte.removeEventListener(tipo, ouvinte);
      fonte.onopen = null;
      fonte.onerror = null;
      fonte.close();
    };
  }, [ativo]);
}
